/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Type-safe Admin REST API Client (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { getAdminDomains } from './urls';

export const ADMIN_AUTH_TOKEN_KEY = 'sophia_admin_access_token';
export const LEGACY_ADMIN_TOKEN_KEY = 'sophia_admin_token';

/**
 * Retrieves the current administrative access token from persistent browser storage,
 * gracefully falling back to legacy keys for compatibility.
 */
export function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem(ADMIN_AUTH_TOKEN_KEY) ||
    localStorage.getItem(LEGACY_ADMIN_TOKEN_KEY) ||
    null
  );
}

/**
 * Persists or clears the admin access token across both current and legacy storage keys.
 */
export function setAdminToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, token);
    localStorage.setItem(LEGACY_ADMIN_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
    localStorage.removeItem(LEGACY_ADMIN_TOKEN_KEY);
  }
}

export interface ApiFetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Universal authenticated fetch helper for Admin API
 */
export async function adminFetch<T = any>(endpoint: string, options: ApiFetchOptions = {}): Promise<T> {
  const { params, headers, ...customConfig } = options;
  const baseUrl = getAdminDomains().apiOrigin;

  let url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        searchParams.append(key, String(val));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  const token = getAdminToken();

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...customConfig,
    headers: {
      ...defaultHeaders,
      ...(headers as Record<string, string>),
    },
  });

  if (response.status === 401) {
    if (typeof window !== 'undefined') {
      setAdminToken(null);
      window.dispatchEvent(new CustomEvent('sophia:admin-unauthorized'));
    }
  }

  let data: any;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const errorCode = data?.error?.code || data?.code || `HTTP_${response.status}`;
    const errorMessage = data?.error?.message || data?.message || response.statusText || 'Unknown error occurred';
    throw new ApiError(response.status, errorCode, errorMessage, data?.error?.details || data?.details);
  }

  return data as T;
}

export const adminClient = {
  get: <T = any>(endpoint: string, params?: Record<string, any>, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, { ...options, method: 'GET', params }),

  post: <T = any>(endpoint: string, body?: any, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, { ...options, method: 'DELETE' }),
};
